import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { createLogger } from "debug/DebugingLogger";
import { TeamAccess } from "target_wasm";
import { TeamPage } from "./TeamPage";
import { LoadingState } from "components/common/LoadingState";
import { useTeamsStore } from "components/main/stores/teamsStore";
import { useAppInitializationStore } from "components/main/stores/appInitializationStore";
import { useShallow } from "zustand/react/shallow";
import { showNotificationGlobal } from "stores/useNotificationStore";
const log = createLogger("TeamPageWrapper");

export const TeamPageWrapper = () => {
  const { team_id } = useParams();
  const navigate = useNavigate();
  const prevTeamIdRef = useRef<string | null>(null);
  const { teams, fetchTeamsList } = useTeamsStore(
    useShallow((state) => ({
      teams: state.teams,
      fetchTeamsList: state.fetchTeamsList,
    })),
  );
  const { teamsInitialized, isAppInitialized } = useAppInitializationStore(
    useShallow((state) => ({
      teamsInitialized: state.teamsInitialized,
      isAppInitialized: state.isAppInitialized(),
    })),
  );
  const [team, setTeam] = useState<TeamAccess | null>(null);

  useEffect(() => {
    if (!teamsInitialized) {
      fetchTeamsList({ showLoading: false }).catch((err) => {
        log.error("Failed to fetch teams:", err);
      });
    }
  }, [teamsInitialized, fetchTeamsList]);

  useEffect(() => {
    if (!team_id || !teamsInitialized) {
      setTeam(null);
      return;
    }

    const foundTeam = teams.find((t) => t.team_id_str() === team_id);
    if (foundTeam) {
      setTeam(foundTeam);
      prevTeamIdRef.current = team_id;
    } else if (prevTeamIdRef.current === team_id) {
      // Team was previously found but is now gone (deleted/left)
      setTeam(null);
      navigate("/teams", { replace: true });
    } else {
      // Team never loaded (bad URL or no access)
      setTeam(null);
      showNotificationGlobal("Team not found or access denied", "error");
      navigate("/teams", { replace: true });
    }
  }, [team_id, teams, teamsInitialized, navigate]);

  if (!isAppInitialized) {
    return null;
  }

  if (!teamsInitialized) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoadingState size="large" isLoading={true} label="Loading team..." />
      </div>
    );
  }

  if (!team) {
    return null;
  }

  return <TeamPage team={team} />;
};
